import { DateTime } from 'luxon';
import {
  enums,
  PhotoModel,
  VisitModel,
  VisitNoteModel,
  VisitWellnessExamModel,
  DiagnosisModel,
  SalesItemPriceRuleModel,
  SalesItemTraitModel,
  LabOrderModel,
  SalesItemOptionModel,
  SalesItemAttributeModel,
  VisitSalesItemModel,
  PetDiagnosisModel,
  SalesRequirementModel,
} from '@common/models';
import { VisitSectionTypes } from '../Enums';
import { convertToNumber } from '@common/utils/numberUtils';

export interface IExamFollowupVisit {
  visitNotes: VisitNoteModel[] | undefined;
  followupNote: VisitNoteModel | undefined;
}
export interface IHasSectionVersion {
  sectionRowVersion: string;
}
export interface IHasFollowupVisitNotes extends IHasSectionVersion {
  followupVisit: IExamFollowupVisit;
}
export interface IExamAssignment extends IHasSectionVersion {
  vetId: number | undefined;
  room: string | undefined;
  reservationAssignmentStatusId: number | undefined;
  dispensingAuthUserId: number | undefined;
  dispensingUserFirstName: string | undefined;
  dispensingUserLastName: string | undefined;
  appointmentTypeId: number | undefined;
}
export interface IWellnessExamCategory {
  examCategoryId: number;
  name: string;
  visitExamResultId: number | undefined;
  resultType: enums.ExamResultType | undefined;
  observationIds: number[];
  notes: string | undefined;
}

export interface IWellnessExamIntake extends IHasFollowupVisitNotes {
  reasonForVisit: string | undefined;
  appointmentNotes: string | undefined;
  intakeNotes: string | undefined;
}
export interface IWellnessExamVital {
  sortOrder: number;
  name: string;
  required: boolean;
  inputType: 'text' | 'number' | 'decimal';
  label: string;
  value: number | string | undefined;
  valueSuffix: string | undefined;
  unableToEvaluate: boolean | undefined;
  min: number | undefined;
  max: number | undefined;
  didNotEvaluate: boolean | undefined;
}

export interface IHistoricWellnessExamVitals {
  visitDate: DateTime | undefined;
  categories: IWellnessExamVital[];
  notes: string | undefined;
}
export interface IWellnessExamVitals
  extends IHistoricWellnessExamVitals,
    IHasFollowupVisitNotes {}
export interface IWellnessExamSubjective extends IHasFollowupVisitNotes {
  notes: string | undefined;
}
export interface IWellnessExamObjectiveWithoutNotes extends IHasSectionVersion {
  categories: IWellnessExamCategory[];
}

export interface IWellnessExamObjective
  extends IWellnessExamObjectiveWithoutNotes,
    IHasFollowupVisitNotes {
  notes: string | undefined;
}

export interface IWellnessExamAssessmentDiagnosis
  extends IHasFollowupVisitNotes {
  diagnoses: DiagnosisModel[];
  petDiagnoses: PetDiagnosisModel[];
  notes: string | undefined;
  petDiagnosisSpecifiedToVisit: PetDiagnosisModel[];
}
export interface IWellnessExamPlan extends IHasFollowupVisitNotes {
  notes: string | undefined;
}
export interface IWellnessExamPrognosis extends IHasFollowupVisitNotes {
  score: number | undefined; // for radio button value
  notes: string | undefined;
}
export interface IWellnessExamDischarge extends IHasFollowupVisitNotes {
  notes: string | undefined;
}
export interface IWellnessExamPrivateNotes extends IHasFollowupVisitNotes {
  notes: string | undefined;
}
export interface IWellnessExamSignature extends IHasSectionVersion {
  veterinarianFullName: string;
  signatureAppliedToVisit: PhotoModel | undefined;
  veterinarianLicenseNumber: string | undefined;
  vetClinicSignature: PhotoModel | undefined;
  hasVetAttestation: boolean;
}
export interface IWellnessExamSalesItem {
  salesItemId: number;
  salesItemOptionId: number;
  salesItemTypeId: number;
  name: string;
  option: string;
  price: number;
  dispensingFee: number | undefined;
  sortOrder: number;
  searchPattern: string;
  salesSubCategoryId: number;
  priceRules?: SalesItemPriceRuleModel[] | undefined;
  hospitalSKU?: string | undefined;
  petcoSKU?: string | undefined;
  traits?: SalesItemTraitModel[];
  salesItemUpgradeId?: number | undefined;
  reminderDays?: number | undefined;
  helpPhoto?: PhotoModel;
  optionModel?: SalesItemOptionModel;
  isPricedByOption?: boolean;
  salesItemAttributes: SalesItemAttributeModel[];
  salesItemRequirements?: SalesRequirementModel[] | undefined;
}

export interface IWellnessExamVisitBuilder extends IHasFollowupVisitNotes {
  vsi: VisitSalesItemModel[];
  totalAmount: number;
  salesPackagePrice: number | undefined;
  subtotalPreDiscountsAmount: number;
  discountAmount: number;
  subtotalPostDiscountsAmount: number;
  taxAmount: number;
}

export interface IWellnessExamPostVisit extends IHasFollowupVisitNotes {}

export interface IWellnessExamDiagnostics extends IHasSectionVersion {
  notes: string | undefined;
}

export interface IWellnessExamForm {
  assignment: IExamAssignment;
  intake: IWellnessExamIntake;
  vitals: IWellnessExamVitals;
  historicalVitals: IHistoricWellnessExamVitals[];
  subjective: IWellnessExamSubjective;
  objectiveWithoutNotes: IWellnessExamObjectiveWithoutNotes;
  objective: IWellnessExamObjective;
  assessment: IWellnessExamAssessmentDiagnosis;
  plan: IWellnessExamPlan;
  prognosis: IWellnessExamPrognosis;
  discharge: IWellnessExamDischarge;
  privateNotes: IWellnessExamPrivateNotes;
  signature: IWellnessExamSignature;
  visitBuilder: IWellnessExamVisitBuilder;
  postVisit: IWellnessExamPostVisit;
  diagnostics: IWellnessExamDiagnostics;
}

export class WellnessExamForm implements IWellnessExamForm {
  assignment: IExamAssignment;
  intake: IWellnessExamIntake;
  vitals: IWellnessExamVitals;
  historicalVitals: IHistoricWellnessExamVitals[];
  subjective: IWellnessExamSubjective;
  objectiveWithoutNotes: IWellnessExamObjectiveWithoutNotes;
  objective: IWellnessExamObjective;
  assessment: IWellnessExamAssessmentDiagnosis;
  plan: IWellnessExamPlan;
  prognosis: IWellnessExamPrognosis;
  discharge: IWellnessExamDischarge;
  privateNotes: IWellnessExamPrivateNotes;
  signature: IWellnessExamSignature;
  visitBuilder: IWellnessExamVisitBuilder;
  postVisit: IWellnessExamPostVisit;
  diagnostics: IWellnessExamDiagnostics;

  constructor(visit: VisitModel, timeZone: string) {
    this.assignment = {
      vetId: visit.veterinarianId,
      room: visit.room,
      reservationAssignmentStatusId:
        visit?.reservation?.reservationAssignmentStatusId,
      dispensingAuthUserId: visit.dispensingAuthUserId,
      dispensingUserFirstName: visit.dispensingUserFirstName,
      dispensingUserLastName: visit.dispensingUserLastName,
      appointmentTypeId: visit.appointmentType?.appointmentTypeId,
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Assignment,
      })!.rowVersion,
    };
    this.intake = {
      reasonForVisit: visit.reasonForVisit,
      appointmentNotes: visit.notes,
      intakeNotes: visit.visitWellnessExam?.intakeNotes,
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.Intake,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.Intake,
        ),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Intake,
      })!.rowVersion,
    };
    this.vitals = {
      ...WellnessExamForm.ToExamVitals(visit.visitWellnessExam),
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.Intake,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.Intake,
        ),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Vitals,
      })!.rowVersion,
    };
    this.historicalVitals = visit.historicalVisitWellnessExams.map((e) =>
      WellnessExamForm.ToExamVitals(e),
    );
    this.subjective = {
      notes: visit.visitWellnessExam?.subjectiveNotes,
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.Subjective,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.Subjective,
        ),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Subjective,
      })!.rowVersion,
    };
    this.objective = {
      categories: visit.examResults.map((er) => ({
        examCategoryId: er.examCategoryId,
        name: er.examCategory.name,
        visitExamResultId: er.visitExamResultId,
        resultType: er.examResultTypeId,
        observationIds: er.observations.map((o) => o.examObservationId) || [],
        notes: er.notes,
      })),
      notes: visit.visitWellnessExam?.objectiveNotes,
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.Objective,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.Objective,
        ),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Objective,
      })!.rowVersion,
    };
    this.objectiveWithoutNotes = {
      categories: visit.examResults.map((er) => ({
        examCategoryId: er.examCategoryId,
        name: er.examCategory.name,
        visitExamResultId: er.visitExamResultId,
        resultType: er.examResultTypeId,
        observationIds: er.observations.map((o) => o.examObservationId) || [],
        notes: er.notes,
      })),
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Objective,
      })!.rowVersion,
    };
    this.assessment = {
      diagnoses: visit.visitDiagnoses.map(
        (visitDiagnosis) => visitDiagnosis.diagnosis,
      ),
      petDiagnoses: visit.petDiagnoses,
      petDiagnosisSpecifiedToVisit: visit.getPetDiagnosesSpecifiedToVisit(
        visit.petDiagnoses,
        timeZone,
      ),
      notes: visit.visitWellnessExam?.notes,
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.Assessment,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.Assessment,
        ),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Assessment,
      })!.rowVersion,
    };
    this.plan = {
      notes: visit.visitWellnessExam?.planNotes,
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.Plan,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.Plan,
        ),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Plan,
      })!.rowVersion,
    };
    this.prognosis = {
      score: visit.visitWellnessExam?.prognosisScoreId,
      notes: visit.visitWellnessExam?.prognosisNotes,
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.Prognosis,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.Prognosis,
        ),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Prognosis,
      })!.rowVersion,
    };
    this.discharge = {
      notes: visit.visitWellnessExam?.dischargeNotes,
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.Discharge,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.Discharge,
        ),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Discharge,
      })!.rowVersion,
    };
    this.privateNotes = {
      notes: visit.visitWellnessExam?.privateNotes,
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.Private,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.Private,
        ),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.PrivateNotes,
      })!.rowVersion,
    };
    this.signature = {
      veterinarianFullName: `${visit.veterinarianFirstName} ${visit.veterinarianLastName}`,
      signatureAppliedToVisit: visit.veterinarianSignaturePhoto,
      vetClinicSignature: visit.clinicVetSignaturePhoto,
      veterinarianLicenseNumber: visit.veterinarianLicenseNumber,
      hasVetAttestation: visit.hasVetAttestation,
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.Signature,
      })!.rowVersion,
    };
    this.visitBuilder = {
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(
          enums.VisitNoteType.VisitBuilder,
        ),
        followupNote: VisitNoteModel.fromVisit(
          visit.visitId,
          enums.VisitNoteType.VisitBuilder,
        ),
      },
      vsi: visit.visitSalesItems,
      totalAmount: visit.totalAmount,
      salesPackagePrice: visit.salesPackagePrice,
      subtotalPreDiscountsAmount: visit.subtotalPreDiscountsAmount,
      discountAmount: visit.discountAmount,
      subtotalPostDiscountsAmount: visit.subtotalPostDiscountsAmount,
      taxAmount: visit.taxAmount,
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.VisitBuilder,
      })!.rowVersion,
    };
    this.postVisit = {
      followupVisit: {
        visitNotes: visit.filterVisitNotesByVisitNoteType(undefined),
        followupNote: VisitNoteModel.fromVisit(visit.visitId, undefined),
      },
      sectionRowVersion: _.find(visit.visitSectionVersions, {
        visitSectionTypeId: VisitSectionTypes.PostVisit,
      })!.rowVersion,
    };
    this.diagnostics = {
      notes: visit.labOrders
        .map((lo) => WellnessExamForm.DiagnosticMarkup(lo))
        .join(''),
      sectionRowVersion: '', // Diagnostic section does not have VisitSectionVersions
    };
  }

  static ToExamVitals(
    model: VisitWellnessExamModel,
  ): IHistoricWellnessExamVitals {
    return {
      visitDate: model.visitDate,
      notes: model.vitalsNotes,
      categories: [
        {
          sortOrder: 1,
          name: 'weight',
          required: true,
          inputType: 'number',
          label: 'Weight (lbs)',
          value: model.weight,
          unableToEvaluate: undefined,
          min: 0,
          max: 9999.99,
          valueSuffix: undefined,
          didNotEvaluate: undefined,
        },
        {
          sortOrder: 2,
          name: 'temp',
          required: false,
          inputType: 'number',
          label: 'Temperature (°F)',
          value: model.temperature,
          unableToEvaluate: model.temperatureUTE,
          min: 0,
          max: 1000,
          valueSuffix: '°F',
          didNotEvaluate: model.temperatureDNE,
        },
        {
          sortOrder: 3,
          name: 'pulse',
          required: false,
          inputType: 'number',
          label: 'Pulse (bpm)',
          value: model.pulse,
          unableToEvaluate: model.pulseUTE,
          min: 0,
          max: 1000,
          valueSuffix: undefined,
          didNotEvaluate: model.pulseDNE,
        },
        {
          sortOrder: 4,
          name: 'respiration',
          required: false,
          inputType: 'number',
          label: 'Respiration (bpm)',
          value: model.respiration,
          unableToEvaluate: model.respirationUTE,
          min: 0,
          max: 1000,
          valueSuffix: undefined,
          didNotEvaluate: model.respirationDNE,
        },
        {
          sortOrder: 5,
          name: 'attitude',
          required: false,
          inputType: 'text',
          label: 'Attitude',
          value: model.attitude,
          unableToEvaluate: model.attitudeUTE,
          min: undefined,
          max: undefined,
          valueSuffix: undefined,
          didNotEvaluate: model.attitudeDNE,
        },
        {
          sortOrder: 6,
          name: 'pain-score',
          required: false,
          inputType: 'number',
          label: 'Pain Score (0-10)',
          value: model.painScale,
          unableToEvaluate: model.painScaleUTE,
          min: 0,
          max: 10,
          valueSuffix: undefined,
          didNotEvaluate: model.painScaleDNE,
        },
        {
          sortOrder: 7,
          name: 'mucous-membrane',
          required: false,
          inputType: 'text',
          label: 'Mucous Membrane',
          value: model.mucousMembrane,
          unableToEvaluate: model.mucousMembraneUTE,
          min: undefined,
          max: undefined,
          valueSuffix: undefined,
          didNotEvaluate: model.mucousMembraneDNE,
        },
        {
          sortOrder: 8,
          name: 'capillary-refill',
          required: false,
          inputType: 'text',
          label: 'Capillary Refill',
          value: model.capillaryRefill,
          unableToEvaluate: model.capillaryRefillUTE,
          min: undefined,
          max: undefined,
          valueSuffix: undefined,
          didNotEvaluate: model.capillaryRefillDNE,
        },
        {
          sortOrder: 9,
          name: 'body-condition',
          required: false,
          inputType: 'number',
          label: 'BCS (1-9)',
          value: model.bodyConditionScore,
          unableToEvaluate: model.bodyConditionScoreUTE,
          min: 1,
          max: 9,
          valueSuffix: undefined,
          didNotEvaluate: model.bodyConditionScoreDNE,
        },
      ],
    };
  }

  static ParseUpdateModel(
    model: VisitWellnessExamModel,
    form: IWellnessExamForm,
  ): VisitWellnessExamModel {
    return {
      ...model,
      notes: form.assessment.notes,
      weight: convertToNumber(form.vitals.categories[0].value),
      temperature: convertToNumber(form.vitals.categories[1].value),
      temperatureUTE: form.vitals.categories[1].unableToEvaluate,
      temperatureDNE: form.vitals.categories[1].didNotEvaluate,
      pulse: convertToNumber(form.vitals.categories[2].value),
      pulseUTE: form.vitals.categories[2].unableToEvaluate,
      pulseDNE: form.vitals.categories[2].didNotEvaluate,
      respiration: convertToNumber(form.vitals.categories[3].value),
      respirationUTE: form.vitals.categories[3].unableToEvaluate,
      respirationDNE: form.vitals.categories[3].didNotEvaluate,
      attitude: form.vitals.categories[4].value?.toString(),
      attitudeUTE: form.vitals.categories[4].unableToEvaluate,
      attitudeDNE: form.vitals.categories[4].didNotEvaluate,
      painScale: convertToNumber(form.vitals.categories[5].value),
      painScaleUTE: form.vitals.categories[5].unableToEvaluate,
      painScaleDNE: form.vitals.categories[5].didNotEvaluate,
      mucousMembrane: form.vitals.categories[6].value?.toString(),
      mucousMembraneUTE: form.vitals.categories[6].unableToEvaluate,
      mucousMembraneDNE: form.vitals.categories[6].didNotEvaluate,
      capillaryRefill: form.vitals.categories[7].value?.toString(),
      capillaryRefillUTE: form.vitals.categories[7].unableToEvaluate,
      capillaryRefillDNE: form.vitals.categories[7].didNotEvaluate,
      bodyConditionScore: convertToNumber(form.vitals.categories[8].value),
      bodyConditionScoreUTE: form.vitals.categories[8].unableToEvaluate,
      bodyConditionScoreDNE: form.vitals.categories[8].didNotEvaluate,
      intakeNotes: form.intake.intakeNotes,
      subjectiveNotes: form.subjective.notes,
      objectiveNotes: form.objective.notes,
      vitalsNotes: form.vitals.notes,
      planNotes: form.plan.notes,
      prognosisNotes: form.prognosis.notes,
      dischargeNotes: form.discharge.notes,
      privateNotes: form.privateNotes?.notes,
      prognosisScoreId: form.prognosis.score,
    };
  }

  static DiagnosticMarkup = ({
    tests,
    reviewedBy,
    communicatedBy,
    notes,
  }: LabOrderModel) => {
    const items = [
      {
        title: 'Diagnostic(s) Ordered',
        value: _.sortBy(tests, (t) => t.testName.toLowerCase())
          .map((t) => t.testName)
          .join(', '),
      },
      { title: 'Reviewed By', value: reviewedBy },
      { title: 'Communicated By', value: communicatedBy },
      { title: 'Notes', value: notes },
    ];

    let markup = '<p>';
    markup += items
      .filter((i) => i.value)
      .map((i) => `<strong>${i.title}:&nbsp;</strong>${i.value}&nbsp;`)
      .join('');
    markup += '</p>';
    return markup;
  };
}
